import {
  applicationsSearchFromLoanOpsFilters,
  type LoanOpsChatSessionSummary,
  type LoanOpsChatUiActions,
} from '../../api/loanOpsChat';

export const CHAT_STORAGE_KEY = 'loan-ops-chat-panel-prefs';
export const CHAT_HISTORY_WIDTH = 232;

export const CHAT_MIN_WIDTH = 360;
export const CHAT_MAX_WIDTH = 760;
export const CHAT_MIN_HEIGHT = 420;
export const CHAT_MAX_HEIGHT = 860;

export type ChatPanelPrefs = {
  open: boolean;
  width: number;
  height: number;
  expanded: boolean;
  historyOpen: boolean;
};

const DEFAULT_PREFS: ChatPanelPrefs = {
  open: false,
  width: 460,
  height: 620,
  expanded: false,
  historyOpen: false,
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

export function loadChatPrefs(): ChatPanelPrefs {
  try {
    const raw = localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw) as Partial<ChatPanelPrefs>;
    return {
      open: Boolean(p.open),
      width: clamp(Number(p.width) || DEFAULT_PREFS.width, CHAT_MIN_WIDTH, CHAT_MAX_WIDTH),
      height: clamp(Number(p.height) || DEFAULT_PREFS.height, CHAT_MIN_HEIGHT, CHAT_MAX_HEIGHT),
      expanded: Boolean(p.expanded),
      historyOpen: Boolean(p.historyOpen),
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function saveChatPrefs(prefs: ChatPanelPrefs): void {
  try {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* private mode / quota — preferences are best-effort */
  }
}

export function errorMessageFromChatFailure(err: unknown): string {
  const anyErr = err as {
    response?: { status?: number; data?: { error?: { message?: string }; message?: string } };
    message?: string;
    code?: string;
  };
  const status = anyErr?.response?.status;
  const apiMsg =
    anyErr?.response?.data?.error?.message || anyErr?.response?.data?.message || anyErr?.message;

  if (status === 401 || status === 403) {
    return 'Session expired or not authorized. Sign in again, then retry.';
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'Loan-ops assistant is unavailable. Check orchestrator and assistant services, then try again.';
  }
  if (
    anyErr?.code === 'ERR_NETWORK' ||
    /network|failed to fetch|econnrefused/i.test(String(apiMsg || ''))
  ) {
    return 'Could not reach the chat service. Check that orchestrator and loan-ops-assistant are running.';
  }
  return String(apiMsg || 'Could not complete that chat request. Please try again.');
}

const FILTER_LABELS: Array<{ keys: string[]; label: string; kind: 'value' | 'from' | 'to' }> = [
  { keys: ['status'], label: 'Status', kind: 'value' },
  { keys: ['stuckStep', 'stuck_step'], label: 'Stuck at', kind: 'value' },
  { keys: ['createdFrom', 'created_from'], label: 'Created from', kind: 'from' },
  { keys: ['createdTo', 'created_to'], label: 'Created to', kind: 'to' },
  { keys: ['updatedFrom', 'updated_from'], label: 'Updated from', kind: 'from' },
  { keys: ['updatedTo', 'updated_to'], label: 'Updated to', kind: 'to' },
];

/** Human-readable chips for an `apply_filters` payload, so the jump to /applications is explainable. */
export function describeLoanOpsFilters(filters: Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const entry of FILTER_LABELS) {
    const key = entry.keys.find((k) => typeof filters[k] === 'string' && String(filters[k]).trim());
    if (!key) continue;
    const raw = String(filters[key]).trim();
    const value = entry.kind === 'value' ? raw.replace(/_/g, ' ') : raw.slice(0, 10);
    out.push(`${entry.label}: ${value}`);
  }
  return out;
}

export type ChatBubbleExtras = {
  links?: Array<{ applicationId: string; label: string; href: string }>;
  filters?: string[];
  filterSearch?: string;
};

/**
 * Renderable decorations for an assistant turn, derived from its `ui_actions`.
 *
 * Shared by live replies and by sessions rehydrated from the DB — deriving this in
 * only one of those paths is why reloaded chats lost their chips and links.
 */
export function chatBubbleExtrasFromUiActions(
  ua: LoanOpsChatUiActions | undefined,
): ChatBubbleExtras {
  if (!ua) return {};

  if (ua.type === 'clarify_candidates' && Array.isArray(ua.candidates)) {
    const links = ua.candidates.map((c) => ({
      applicationId: c.applicationId,
      label: c.displayName || c.applicationId,
      href: c.admin_url || `/applications/${c.applicationId}`,
    }));
    return links.length ? { links } : {};
  }

  if (ua.type === 'apply_filters' && ua.filters) {
    const described = describeLoanOpsFilters(ua.filters);
    const search = applicationsSearchFromLoanOpsFilters(ua.filters).toString();
    return {
      filters: described.length ? described : undefined,
      filterSearch: search || undefined,
    };
  }

  return {};
}

export type SessionGroup = { label: string; sessions: LoanOpsChatSessionSummary[] };

/** Group sessions into Today / Yesterday / Previous 7 days / Earlier, newest first. */
export function groupSessionsByRecency(
  sessions: LoanOpsChatSessionSummary[],
  now: Date = new Date(),
): SessionGroup[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 86_400_000;

  const buckets: SessionGroup[] = [
    { label: 'Today', sessions: [] },
    { label: 'Yesterday', sessions: [] },
    { label: 'Previous 7 days', sessions: [] },
    { label: 'Earlier', sessions: [] },
  ];

  const sorted = [...sessions].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  for (const s of sorted) {
    const t = new Date(s.updatedAt).getTime();
    if (Number.isNaN(t)) {
      buckets[3].sessions.push(s);
    } else if (t >= startOfToday) {
      buckets[0].sessions.push(s);
    } else if (t >= startOfToday - dayMs) {
      buckets[1].sessions.push(s);
    } else if (t >= startOfToday - 7 * dayMs) {
      buckets[2].sessions.push(s);
    } else {
      buckets[3].sessions.push(s);
    }
  }

  return buckets.filter((b) => b.sessions.length > 0);
}

/** Compact relative stamp for transcript/history meta lines. */
export function formatChatTimestamp(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString();
}
